import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { authService } from '../services/authService';
import { redirectToIntus } from '../utils/intusRedirect';

/**
 * SSO desde Vita360: el paciente entra con ?token=<sso>.
 * Intercambia el token por la sesión propia (POST /auth/sso), descarta el
 * token de la URL y entra DIRECTO al dashboard de la app.
 *
 * El token SSO es de UN SOLO USO. React StrictMode (dev) monta/desmonta el
 * componente dos veces, disparando el efecto 2× con el mismo token: la 2ª
 * llamada falla (token ya consumido) y redirigiría al portal recién entrado.
 * `processedTokenRef` garantiza que cada token se intercambie una sola vez.
 *
 * NAVEGACIÓN: se usa una recarga completa (`window.location.replace`), NO
 * `navigate()` del router. Motivo (bug real): al hacer `setAuth` + limpiar el
 * `?token`, el store (useSyncExternalStore) se re-renderiza con prioridad
 * SÍNCRONA antes de que el router aplique la ruta nueva. En esa pasada
 * `IntusRedirect` sigue montado en "/", ya sin token y con isLoading=false, así
 * que dispara `redirectToIntus()` y el paciente vuelve a la bienvenida. La
 * recarga completa desmonta `IntusRedirect` de forma atómica y carga /dashboard
 * con la sesión ya persistida en localStorage. Mismo patrón que
 * `ProfessionalSsoHandler`.
 */
export default function SsoAuthHandler() {
  const { setAuth, setLoading } = useAuthStore();
  const location = useLocation();
  const processedTokenRef = useRef<string | null>(null);

  useEffect(() => {
    const token = new URLSearchParams(location.search).get('token');

    if (!token) {
      return;
    }

    // Evitar doble intercambio del mismo token (StrictMode en dev).
    if (processedTokenRef.current === token) {
      return;
    }
    processedTokenRef.current = token;

    setLoading(true);

    authService
      .ssoLogin(token)
      .then((res) => {
        // Registrar la sesión en el store persistido ANTES de navegar.
        setAuth(res.usuario as never, res.accessToken, res.refreshToken, res.intusBaseUrl);

        // Navegación de página completa: descarta IntusRedirect del árbol y
        // evita la race descrita arriba.
        window.location.replace(`${window.location.origin}/dashboard`);
      })
      .catch(() => {
        // Token rechazado: de vuelta al portal de Vita360 (el login admin es secreto).
        redirectToIntus();
      })
      .finally(() => setLoading(false));
  }, [location.search, setAuth, setLoading]);

  return null;
}
